import { useEffect, useRef, useState } from "react";
import { t } from "@/i18n-js/instance";
import { useChatMessagesV2Api } from "@/react/hooks/chatsV2/useChatMessagesV2Api";
import type { SubscriptionStateType } from "@/react/hooks/subscriptionEventsEmitter";
import { subscriptionEventsEmitter } from "@/react/hooks/subscriptionEventsEmitter";
import { useToast } from "@circle-react-uikit/ToastV2";

const TIME_THRESHOLD = 3000;

const isMessagingAreaVisible = () => {
  const messageElement =
    document.getElementById("message-thread-scroll-view") ||
    document.getElementById("message-scroll-view");

  return Boolean(messageElement?.offsetParent);
};

export const useRefetchMessagesOnReconnection = ({
  uuid,
  isLiveStream,
}: {
  uuid: string;
  isLiveStream: boolean;
}) => {
  const previousSubscriptionStateRef = useRef<string>();
  const disconnectionTimestampRef = useRef<number>();
  const subscriptionStateRef = useRef<SubscriptionStateType>("connected");
  const toastr = useToast();
  const [subscriptionState, setSubscriptionState] =
    useState<SubscriptionStateType>("connected");
  const { fetchChatMessagesFirstPage } = useChatMessagesV2Api();

  const shouldShowToastMessage = !isLiveStream || isMessagingAreaVisible();

  const handleSubscriptionStateChange = ({
    subscriptionState: stateEmited,
  }: {
    subscriptionState: SubscriptionStateType;
  }) => {
    setSubscriptionState(stateEmited);
  };

  useEffect(() => {
    subscriptionEventsEmitter.on(
      "chatRoomSubscriptionStateChange",
      handleSubscriptionStateChange,
    );

    return () => {
      subscriptionEventsEmitter.off(
        "chatRoomSubscriptionStateChange",
        handleSubscriptionStateChange,
      );
    };
  }, []);

  useEffect(() => {
    subscriptionStateRef.current = subscriptionState;
  }, [subscriptionState]);

  useEffect(() => {
    if (subscriptionState === "disconnected") {
      disconnectionTimestampRef.current = Date.now();
      setTimeout(() => {
        if (
          subscriptionStateRef.current === "disconnected" &&
          shouldShowToastMessage
        ) {
          toastr.error(t("websocket_message.unstable_right_now"));
        }
      }, TIME_THRESHOLD);
    } else if (
      previousSubscriptionStateRef.current === "disconnected" &&
      subscriptionState === "connected"
    ) {
      const timeDifference =
        Date.now() - (disconnectionTimestampRef.current ?? 0);

      if (timeDifference > TIME_THRESHOLD && shouldShowToastMessage) {
        toastr.success(t("websocket_message.back_online"), {
          shouldUseProgress: false,
        });
      }
      void fetchChatMessagesFirstPage();
    }
    previousSubscriptionStateRef.current = subscriptionState;
  }, [subscriptionState, uuid]);
};
